/**
 * What the customer pays, and when.
 *
 * Four slots exist on every estimate (First / Second / Third / Final) and the
 * rep types the first three. The Final is never typed: it is whatever is left,
 *
 *     final = total - (first + second + third)
 *
 * so the schedule adds up to the contract by construction rather than by
 * somebody checking. That is the whole reason this file exists — a payment
 * schedule that does not sum to the total is a customer arguing on a doorstep
 * about a number the company printed itself.
 *
 * Two consequences worth stating, because they surprise people reading the
 * table directly:
 *
 * 1. **The Final slot holds no amount in the database.** It is derived here on
 *    every read. A stored copy would go stale the moment a line item moved,
 *    and a stale payment schedule is worse than none.
 * 2. **A null amount means "not set", and is not the same as zero.** Null takes
 *    the default below; a typed 0 is respected, because "nothing due at
 *    signing, all of it at completion" is a real deal a rep may strike.
 *
 * Under $1,000 there is no split at all — one line, due on completion. The
 * threshold matches the deposit rule the production board already runs on.
 */

import { PAYMENT_SLOT_LABEL } from "./constants.js";
import {
  type Cents,
  addCents,
  cents,
  subCents,
  toCents,
  formatMoney,
} from "./money.js";

/**
 * At or above this, the schedule splits 50/50 by default. Below it, the whole
 * contract is due on completion and there is no first payment to collect.
 */
export const PAYMENT_SPLIT_THRESHOLD = 100_000 as Cents;

/** The two trigger texts the database writes on every new estimate. */
export const TRIGGER_ON_SIGNING = "Due Upon Contract Signed";
export const TRIGGER_ON_COMPLETION = "Due Upon Completion";

/** The slots a rep may type into. The Final is computed, never entered. */
export const EDITABLE_SLOTS = [1, 2, 3] as const;
export const FINAL_SLOT = 4;

/** A payment row as the database holds it — `amount` may be null. */
export interface StoredPayment {
  slot: number;
  amount: number | string | null | undefined;
  trigger_text?: string | null;
}

/** A payment row as it is shown, on the builder and to the customer. */
export interface ScheduleRow {
  slot: number;
  label: string;
  amount: Cents;
  triggerText: string;
  /** False for the Final row, which is always computed. */
  editable: boolean;
  /** True when the amount shown is the 50% default, not a typed figure. */
  isDefault: boolean;
}

export interface PaymentSchedule {
  rows: ScheduleRow[];
  /** total - (first + second + third). Negative is possible, and is an error. */
  final: Cents;
  /** False under the threshold: one line, no first payment. */
  split: boolean;
  /**
   * Why this schedule cannot be sent, in words for a person. Null when it is
   * fine. A schedule with an error must not be saved or presented.
   */
  error: string | null;
}

/**
 * The same four labels the rest of the app uses. Imported rather than repeated:
 * two lists that must agree is one list too many.
 */
const LABELS = PAYMENT_SLOT_LABEL;

const amountOf = (
  payments: readonly StoredPayment[],
  slot: number,
): Cents | null => {
  const row = payments.find((p) => Number(p.slot) === slot);
  if (!row || row.amount == null || row.amount === "") return null;
  return toCents(row.amount);
};

const triggerOf = (
  payments: readonly StoredPayment[],
  slot: number,
  fallback: string,
): string => {
  const row = payments.find((p) => Number(p.slot) === slot);
  const text = row?.trigger_text?.trim();
  return text ? text : fallback;
};

/**
 * Half the contract, rounded to the cent. The odd cent lands on the Final,
 * which absorbs it by being the remainder — the two always sum to the total.
 *
 * **Half of the contract as SIGNED**, where the customer has signed one. Work
 * added by a change order afterwards lands wholly on the Final, and the
 * deposit stays the figure they were billed (ADR 0205).
 *
 * That matters because this is a *default* — a figure nobody typed, worked out
 * afresh on every read. Until 2026-09-23 it was half of whatever the contract
 * came to today, so a change order signed after the deposit had been paid
 * raised the deposit retroactively by half the change, and the Final — the
 * remainder — rose by the other half instead of by the whole of it. The
 * deposit that had already arrived was then counted at the new, larger figure,
 * and the balance came out short by exactly half the change order. Brian
 * Garrett, job 1004: $653 of rotten decking and corner trim, $326.50 never
 * asked for. The change order's own fine print says "Your deposit does not
 * change"; this is the line that has to make that true.
 *
 * `signedContract` is `contractAsSigned` in quickbooks.ts — the same
 * `invoiceAmount`, with the changes signed since left out. Undefined on an
 * estimate nobody has signed, where there is no frozen contract and the
 * schedule follows the builder, exactly as it always has.
 */
export function defaultFirstPayment(
  total: Cents,
  signedContract?: Cents | null,
): Cents {
  if (total < PAYMENT_SPLIT_THRESHOLD) return cents(0);
  // Never more than the contract: an estimate edited down below what was
  // signed would otherwise put the Final into the negative, and a negative
  // Final is an error the schedule refuses to save.
  const base =
    signedContract != null && signedContract > 0
      ? cents(Math.min(signedContract, total))
      : total;
  return cents(Math.round(base / 2));
}

/**
 * What the rep typed into the first slot, or null when they typed nothing.
 *
 * Null and zero are different answers — zero is "nothing at signing", a deal a
 * rep may strike — and this is the one reading of slot 1 that both
 * `signingFirstPayment` and the presented document, which freezes it, use.
 */
export function typedFirstPayment(
  payments: readonly StoredPayment[],
): Cents | null {
  return amountOf(payments, 1);
}

/**
 * Slot 1 of an option being signed for: the figure the signed document froze,
 * when it froze one, and the live schedule only when it did not.
 *
 * `frozen` is `PresentedPackage.typedFirstPayment` straight off the snapshot:
 * cents, or null for "nothing was typed". Both are what the customer read, and
 * both beat whatever the schedule says now. Undefined is a document frozen
 * before the figure was, or a contract signed on paper, which has no document;
 * then the live schedule is all there is, and it is what signing always billed
 * those by. Anything else is not something we wrote, and reads as undefined.
 *
 * Until 2026-09-11 signing read the live schedule for everyone, so a rep who
 * edited the first payment after presenting billed a figure the customer's
 * signed copy never showed. Transcribed for Deno as `typedAtSigning` in
 * presented-document/first-payment.ts.
 */
export function typedFirstPaymentAtSigning(
  frozen: unknown,
  payments: readonly StoredPayment[],
): Cents | null {
  if (frozen === null) return null;
  if (typeof frozen === "number" && Number.isFinite(frozen)) {
    return cents(Math.round(frozen));
  }
  return typedFirstPayment(payments);
}

/** One option bought on a signature, as its first payment needs it. */
export interface BoughtOption {
  /** What this option is worth. The lead carries the upgrades. */
  total: Cents;
  /** Its own schedule, as stored. Only slot 1 is read. */
  payments: readonly StoredPayment[];
}

/**
 * One option bought on a signature, with slot 1 already read out of its
 * schedule. That is what a presented document holds: it froze the figure, not
 * the schedule (`PresentedPackage.typedFirstPayment`).
 */
export interface TypedOption {
  /** What this option is worth. The lead carries the upgrades. */
  total: Cents;
  /** `typedFirstPayment` of its schedule — null when nothing was typed. */
  typedFirst: Cents | null;
}

/**
 * What a customer is asked for at signing, for everything they bought.
 *
 * Two kinds of option, added together (ADR 0120, ADR 0124):
 *
 *  - **One the rep typed a first payment on** counts that figure — zero too,
 *    because zero is a decision and `paymentSchedule` honours it.
 *  - **Every other one goes in together**, and the default rule — half, once
 *    it reaches the $1,000 split — is applied to their combined total.
 *
 * Combined, not one by one. Jonathon, 2026-09-10, on $800 of gutters and $900
 * of siding bought together, which asked for nothing at signing because each
 * was under the split by itself: "anything 1000 or more should follow the
 * payment rule." $1,700 of work is $850 at signing; $600 and $300 together are
 * still under it, and ask for nothing.
 *
 * A figure sitting in the first slot of an option under the split is a
 * leftover its own schedule refuses (`paymentSchedule` errors on it and the
 * Present screen will not send it), not a decision, so that option goes in with
 * the rest.
 *
 * Until 2026-09-10 the Edge Function had its own version: the sum of the typed
 * figures only when every option had one, and half of the whole thing
 * otherwise. So a rep's typed $0 on the roof was overridden by half the roof
 * the moment the gutters were bought beside it, and the customer was billed a
 * figure no schedule on either estimate showed. That is still true here: a
 * typed figure is never overridden.
 *
 * A whole signature under the split is offered in full and not chased
 * (`dueNow` false): Jonathon, 2026-09-06, "i'm fine with if a customer wants to
 * proactively pay for something." With one option this is exactly what the
 * signing page has always asked. Transcribed for Deno as
 * `firstPaymentForOptions` in presented-document/first-payment.ts.
 */
export function signingFirstPayment(options: readonly BoughtOption[]): {
  amount: Cents;
  dueNow: boolean;
} {
  return signingFirstPaymentTyped(
    options.map((option) => ({
      total: option.total,
      typedFirst: typedFirstPayment(option.payments),
    })),
  );
}

/**
 * `signingFirstPayment`, for a caller holding the typed figure rather than the
 * schedule — the rule itself is written here, once. The customer's proposal
 * prints its deposit through this (`presentedDeposit`), so what the document
 * says is due at signing and what signing bills cannot be two answers.
 */
export function signingFirstPaymentTyped(options: readonly TypedOption[]): {
  amount: Cents;
  dueNow: boolean;
} {
  const contract = addCents(...options.map((option) => option.total));
  if (contract <= 0) return { amount: cents(0), dueNow: false };
  if (contract < PAYMENT_SPLIT_THRESHOLD) {
    return { amount: contract, dueNow: false };
  }

  let typedTotal = cents(0);
  let untyped = cents(0);
  for (const option of options) {
    const typed =
      option.total >= PAYMENT_SPLIT_THRESHOLD ? option.typedFirst : null;
    if (typed != null) {
      typedTotal = addCents(typedTotal, typed < 0 ? cents(0) : typed);
    } else {
      untyped = addCents(untyped, option.total);
    }
  }
  const amount = addCents(typedTotal, defaultFirstPayment(untyped));
  return { amount, dueNow: amount > 0 };
}

/** One option on a signature, before the contract is split across them. */
export interface SignedOption {
  estimateId: string;
  /** Its own total. Read only for options after the first. */
  total: Cents;
  payments: readonly StoredPayment[];
}

/**
 * The signed contract split across what was bought, the way signing splits it.
 *
 * The first option is the lead — `proposal_acceptances.estimate_id`, which the
 * upgrades land on — and is worth the contract less every other option's own
 * total. So the parts add up to the contract to the cent, upgrades included,
 * with nothing left over. `paymentPlan` in presented-document/validation.ts
 * does the same for the Edge Function, and supabase/test/first-payment.test.ts
 * holds the two together. Feed the answer to `signingFirstPayment`.
 */
export function signedContractOptions(
  contract: Cents,
  bought: readonly SignedOption[],
): (BoughtOption & { estimateId: string })[] {
  const [lead, ...rest] = bought;
  if (!lead) return [];
  const others = addCents(...rest.map((option) => option.total));
  return [{ ...lead, total: subCents(contract, others) }, ...rest];
}

/** The Final payment: what is left after the three that were typed. */
export function finalPayment(
  total: Cents,
  first: Cents,
  second: Cents,
  third: Cents,
): Cents {
  return subCents(total, addCents(first, second, third));
}

/**
 * The whole schedule, from the contract total and whatever the rep has typed.
 *
 * Everything that shows a payment schedule goes through this — the estimate
 * builder, and later the presented document and the handoff — so there is one
 * answer to "what does this customer owe and when", not three.
 *
 * The presented document's `presentedSchedule` lays a frozen schedule out
 * across everything the customer bought, so it combines options this cannot,
 * and it reads the same remainder (`finalPayment`). Under the split, where
 * this returns an error for leftover typed amounts, it prints no schedule
 * until the rep clears them (ADR 0267).
 */
export function paymentSchedule(
  total: Cents,
  payments: readonly StoredPayment[],
  /**
   * The contract as the customer signed it, where they have signed one, so
   * that a change order signed afterwards lands wholly on the Final rather
   * than half of it on the deposit (ADR 0205). Omitted on the builder, where
   * nothing is frozen yet and the schedule follows the estimate.
   */
  signedContract?: Cents | null,
): PaymentSchedule {
  const typed = {
    first: amountOf(payments, 1),
    second: amountOf(payments, 2),
    third: amountOf(payments, 3),
  };
  const anyTyped =
    (typed.first ?? 0) !== 0 ||
    (typed.second ?? 0) !== 0 ||
    (typed.third ?? 0) !== 0;

  // Under the threshold there is no split, so there is nothing to lay out: one
  // line, the whole contract, due on completion.
  if (total < PAYMENT_SPLIT_THRESHOLD) {
    // ...unless amounts are still sitting in the first three slots, typed while
    // the job was bigger. Those rows come back — editable — because the error
    // below tells the rep to clear them and a row they cannot see is a row they
    // cannot clear. A clean sub-threshold estimate is still the single line.
    const leftovers = ([1, 2, 3] as const)
      .filter((slot) => (amountOf(payments, slot) ?? 0) !== 0)
      .map((slot) => ({
        slot,
        label: LABELS[slot]!,
        amount: amountOf(payments, slot) ?? cents(0),
        triggerText: triggerOf(
          payments,
          slot,
          slot === 1 ? TRIGGER_ON_SIGNING : "",
        ),
        editable: true,
        isDefault: false,
      }));

    return {
      split: false,
      final: total,
      rows: [
        ...leftovers,
        {
          slot: FINAL_SLOT,
          label: LABELS[FINAL_SLOT]!,
          amount: total,
          triggerText: triggerOf(payments, FINAL_SLOT, TRIGGER_ON_COMPLETION),
          editable: false,
          isDefault: true,
        },
      ],
      // An amount typed while the job was bigger would otherwise sit there
      // unpaid and unmentioned, so say so rather than quietly dropping it.
      // Note the rows above do NOT sum to the total while this is set; that is
      // what the error means, and an errored schedule is never saved or sent.
      error: anyTyped
        ? `Under ${formatMoney(PAYMENT_SPLIT_THRESHOLD)} there is no payment split — clear the First, Second and Third amounts.`
        : null,
    };
  }

  const first = typed.first ?? defaultFirstPayment(total, signedContract);
  const second = typed.second ?? cents(0);
  const third = typed.third ?? cents(0);
  const final = finalPayment(total, first, second, third);

  const editable = [
    {
      slot: 1,
      amount: first,
      typed: typed.first,
      fallback: TRIGGER_ON_SIGNING,
    },
    { slot: 2, amount: second, typed: typed.second, fallback: "" },
    { slot: 3, amount: third, typed: typed.third, fallback: "" },
  ].map(({ slot, amount, typed: entered, fallback }) => ({
    slot,
    label: LABELS[slot]!,
    amount,
    triggerText: triggerOf(payments, slot, fallback),
    editable: true,
    isDefault: entered == null,
  }));

  return {
    split: true,
    final,
    rows: [
      ...editable,
      {
        slot: FINAL_SLOT,
        label: LABELS[FINAL_SLOT]!,
        amount: final,
        triggerText: triggerOf(payments, FINAL_SLOT, TRIGGER_ON_COMPLETION),
        editable: false,
        isDefault: true,
      },
    ],
    error:
      final < 0
        ? `The payments add up to more than the ${formatMoney(total)} contract. Lower one of them by ${formatMoney(cents(Math.abs(final)))}.`
        : null,
  };
}

/**
 * The first payment to collect after signing, or null when there is none.
 *
 * The payment page (§5) redirects to nothing under the threshold, and the
 * handoff needs to know whether a deposit was even asked for.
 */
export function firstPaymentDue(
  total: Cents,
  payments: readonly StoredPayment[],
): Cents | null {
  const schedule = paymentSchedule(total, payments);
  if (!schedule.split) return null;
  const first = schedule.rows.find((r) => r.slot === 1);
  return first && first.amount > 0 ? first.amount : null;
}

/**
 * What the rep typed into a slot, in cents, or null when they typed nothing.
 * The one reading of a stored amount, for the files that freeze it.
 */
export const typedPayment = amountOf;
